/**
 * The resolve-label-error action. Opened from a timeline error row rather than from the actions
 * toolbar, so open() hands the action to the toolbar store itself, the way the items table opens
 * the reject-item popup.
 */
import { makeAutoObservable } from "mobx";
import { TOAST_VARIANTS } from "../../../../../components/toast-messages-modal/toast-message/toast-message";
import toastMessageListStore from "../../../../../components/toast-messages-modal/toast-messages-store";
import { IErrorNoteContent, IShippingDetailsReturnMethod, RMA_STATUS } from "../../rma-types";
import RmaPageStore from "../rma-page-store";
import ChangeShippingOptionStore from "./change-shipping-option-store";
import ValidateShippingDetailsStore from "./validate-shipping-details-store";

/** Screens of the modal. */
export enum RESOLVE_LABEL_ERROR_STEP {
    SELECT_RESOLUTION_PATH = "select-resolution-path",
    VALIDATE_SHIPPING_DETAILS = "validate-shipping-details",
    SELECT_SHIPPING_RATE = "select-shipping-rate",
    CHANGE_SHIPPING_OPTION = "change-shipping-option",
}

/**
 * Shown in place of the carrier's own error when the re-quote produced nothing to approve. It
 * takes the merchant back to where the paths are offered, because the other one may still work.
 */
export const RATES_ERROR_NOTE: IErrorNoteContent = {
    caption: "No shipping rates for these details",
    body: "The carrier returned no rates for the details as they stand. Check them again, or resolve the error by changing the shipping option.",
};

/**
 * The navigation graph of the flow: the screen each step goes Back to, null for the one the modal
 * opens on. The flow is a tree and not a wizard - the resolution paths are branches of different
 * lengths - so a step declares its parent instead of a position in a list of steps.
 */
const STEP_PARENTS: Record<RESOLVE_LABEL_ERROR_STEP, RESOLVE_LABEL_ERROR_STEP | null> = {
    [RESOLVE_LABEL_ERROR_STEP.SELECT_RESOLUTION_PATH]: null,
    [RESOLVE_LABEL_ERROR_STEP.VALIDATE_SHIPPING_DETAILS]: RESOLVE_LABEL_ERROR_STEP.SELECT_RESOLUTION_PATH,
    [RESOLVE_LABEL_ERROR_STEP.SELECT_SHIPPING_RATE]: RESOLVE_LABEL_ERROR_STEP.VALIDATE_SHIPPING_DETAILS,
    [RESOLVE_LABEL_ERROR_STEP.CHANGE_SHIPPING_OPTION]: RESOLVE_LABEL_ERROR_STEP.SELECT_RESOLUTION_PATH,
};

/** Every screen of the flow reads the same store; the modal renders the one of the current step. */
export interface IResolveLabelErrorScreenProps {
    store: ResolveLabelErrorActionStore;
}

class ResolveLabelErrorActionStore {
    private _errorNote: IErrorNoteContent | null = null;
    private _currentStep: RESOLVE_LABEL_ERROR_STEP = RESOLVE_LABEL_ERROR_STEP.SELECT_RESOLUTION_PATH;
    /** One store per resolution path; this one is the "validate shipment details" path. */
    public readonly validateShippingDetails: ValidateShippingDetailsStore;
    /** The other path: keep the details, change the return method the label is bought on. */
    public readonly changeShippingOption: ChangeShippingOptionStore;

    constructor(private _rma: RmaPageStore) {
        this.validateShippingDetails = new ValidateShippingDetailsStore(_rma);
        this.changeShippingOption = new ChangeShippingOptionStore(_rma);
        makeAutoObservable(this, {}, { autoBind: true });
    }

    /**
     * Whether the RMA is in a state where shipment details can still be fixed and the approval
     * retried. Approval resolves shipping-label errors server-side, but an RMA rejected or
     * cancelled after a failed label keeps its unresolved error and would otherwise still offer
     * Resolve. Whether the shop may use the flow at all is answered per error row by the backend.
     */
    public get canResolveShippingLabelErrors(): boolean {
        return this._rma.status === RMA_STATUS.PENDING_APPROVAL
            && !this._hasGeneratedLabel
            && !this._isMultiShipment;
    }

    /**
     * Opens the modal on one error, always starting from the first screen, and prefetches the
     * shipment details right away: one call feeds every resolution path, so it does not belong to
     * whichever step the merchant picks.
     */
    public open(errorNote: IErrorNoteContent | null): void {
        this._errorNote = errorNote;
        this._currentStep = RESOLVE_LABEL_ERROR_STEP.SELECT_RESOLUTION_PATH;
        this.changeShippingOption.reset();
        void this.validateShippingDetails.load();
        this._rma.rmaActionsToolbarStore().setCurrentAction(this._rma.resolveLabelErrorAction());
    }

    /**
     * The error the merchant opened the modal on - or, once a re-quote came back with nothing, the
     * one that says so. The carrier's original error is kept underneath and comes back on the next
     * quote that works, so a failed attempt does not lose what the merchant came here to fix.
     */
    public get errorNote(): IErrorNoteContent | null {
        return this.ratesFailed ? RATES_ERROR_NOTE : this._errorNote;
    }

    /**
     * The return methods the RMA may be moved onto. They arrive with the prefetch - one call feeds
     * every path - so the change shipping option path reads them from here instead of reaching into
     * the store of the other path.
     */
    public get returnMethods(): IShippingDetailsReturnMethod[] {
        return this.validateShippingDetails.shippingDetails?.return_methods ?? [];
    }

    /** Whether the last re-quote left the merchant with nothing to approve. */
    public get ratesFailed(): boolean { return this.validateShippingDetails.shippingRates.failed; }

    public get currentStep(): RESOLVE_LABEL_ERROR_STEP { return this._currentStep; }

    /** False on the screen the modal opens on, where the header shows the title instead of Back. */
    public get canGoBack(): boolean { return STEP_PARENTS[this._currentStep] !== null; }

    public goToStep(step: RESOLVE_LABEL_ERROR_STEP): void {
        this._currentStep = step;
    }

    public goBack(): void {
        const parent = STEP_PARENTS[this._currentStep];
        if (parent) {
            this._currentStep = parent;
        }
    }

    /**
     * The action under the cards. A method a carrier quotes takes the edited details to a rate; one
     * no carrier quotes has no rate to pick, so the details are approved as they stand.
     */
    public async continueFromShippingDetails(): Promise<void> {
        if (this.validateShippingDetails.isQuotableMethod) {
            await this.continueToShippingRate();
            return;
        }

        await this.approve();
    }

    /**
     * Leaves the edited details for the rate they are quoted on, or comes back to the resolution
     * paths when there is no rate to leave for.
     */
    public async continueToShippingRate(): Promise<void> {
        await this.validateShippingDetails.fetchShippingRates();

        this._currentStep = this.ratesFailed
            ? RESOLVE_LABEL_ERROR_STEP.SELECT_RESOLUTION_PATH
            : RESOLVE_LABEL_ERROR_STEP.SELECT_SHIPPING_RATE;
    }

    /**
     * Approves on what the merchant chose, then closes either way: the error is resolved or it is not, and
     * both answers belong on the RMA page rather than in a modal the merchant is still looking at.
     * A failure offers the way back in, because reopening is the only retry there is.
     */
    public async approve(): Promise<void> {
        try {
            await this._approvingPath.approve();
            toastMessageListStore.add({
                title: "Error resolved",
                description: "The shipping label was generated with the details you confirmed.",
                variant: TOAST_VARIANTS.SUCCESS,
            });
        } catch (e) {
            console.error("Failed to approve the RMA from the resolve label error modal", e);
            const toast = toastMessageListStore.add({
                title: "Error could not be resolved",
                description: "The label was not generated. The error is still on the RMA.",
                variant: TOAST_VARIANTS.ERROR,
                actionButton: {
                    title: "Try again",
                    onClick: () => {
                        toast.remove();
                        this.open(this._errorNote);
                    },
                },
            });
        } finally {
            this._close();
            /** Approve moves the RMA on: its status, errors, shipments and timeline all change. */
            void this._rma.loadRma();
        }
    }

    /**
     * The path the merchant is approving from. Each one approves on what it owns - the corrected
     * details, or the method the RMA is moving onto - and what the modal then does with the answer
     * is the same either way.
     */
    private get _approvingPath(): { approve: () => Promise<void> } {
        return this._currentStep === RESOLVE_LABEL_ERROR_STEP.CHANGE_SHIPPING_OPTION
            ? this.changeShippingOption
            : this.validateShippingDetails;
    }

    private _close(): void {
        this._rma.rmaActionsToolbarStore().setCurrentAction(null);
    }

    private get _hasGeneratedLabel(): boolean {
        return this._rma.shipmentList.some((shipment) => !!shipment.returnLabelUrl);
    }

    /**
     * The flow edits one parcel: a single weight and one set of dimensions, which the backend
     * then applies to every label it generates. Until per-shipment parcel details are supported,
     * an RMA with more than one shipment must not reach it.
     */
    private get _isMultiShipment(): boolean {
        return this._rma.shipmentList.length > 1;
    }
}

export default ResolveLabelErrorActionStore;
